import { useAuthStore } from '@/shared/stores/auth'
import { capturePermissionSubject } from '@/stores/permissionsStore'

/** One manager lifetime; credentials and callbacks cannot outlive its current identity. */
export class SocketSessionLease {
  private generation = 0
  private active = false
  private ownsIdentity: () => boolean = () => false
  start(tenantId: string, userId: string): void {
    this.ownsIdentity = capturePermissionSubject(tenantId, userId)
    this.active = true
    this.generation++
  }
  allowsUse(): boolean {
    return this.active && this.ownsIdentity()
  }
  snapshotOwner(): () => boolean {
    const owner = this.ownsIdentity
    return () => this.active && owner === this.ownsIdentity && owner()
  }
  stop(): void {
    this.active = false
    this.generation++
  }
  capture(tenantId: string, userId: string): () => boolean {
    const generation = ++this.generation
    const ownsSubject = capturePermissionSubject(tenantId, userId)
    return () => this.active && generation === this.generation && ownsSubject()
  }
}

export function connectOwnedSocket(
  lease: SocketSessionLease,
  tenantId: string,
  userId: string,
  connect: (token: string | null) => void,
): void {
  const ownsSubject = lease.capture(tenantId, userId)
  void useAuthStore
    .getState()
    .getToken()
    .then(
      (token) => {
        if (ownsSubject()) connect(token)
      },
      () => {
        if (ownsSubject()) connect(null)
      },
    )
}

/** Session ID key in sessionStorage — single source of truth (CLAUDE.md #4). */
const ENGINE_SESSION_KEY = 'axiomgo-engine-session-id'

/**
 * SOC-012 / STO-266: sentinela do subprotocolo que carrega o JWT no handshake
 * do WebSocket, fora da query string. Contraparte no motor:
 * `extractHandshakeToken`/`HANDSHAKE_TOKEN_SUBPROTOCOL`, `src/gateway/server.ts`
 * (STO-259, PR #2313) — o valor tem de casar exatamente com o de lá.
 */
const WS_HANDSHAKE_TOKEN_SUBPROTOCOL = 'axiomgo-jwt'

export function createAuthenticatedSocket(wsUrl: string, token: string | null): WebSocket {
  const params = new URLSearchParams()
  const sessionId = sessionStorage.getItem(ENGINE_SESSION_KEY)
  if (sessionId) params.set('sessionId', sessionId)
  const qs = params.toString()
  const url = qs ? `${wsUrl}?${qs}` : wsUrl
  return token ? new WebSocket(url, [WS_HANDSHAKE_TOKEN_SUBPROTOCOL, token]) : new WebSocket(url)
}

/** Browser 1006 has no peer close frame; never turn it into an invented HTTP reason. */
export function socketCloseDiagnostic(
  event: Pick<CloseEvent, 'code' | 'reason' | 'wasClean'>,
  wsUrl: string,
  opened: boolean,
  attempt: number,
) {
  let host = '[invalid-host]'
  try {
    host = new URL(wsUrl).host
  } catch {
    /* No credential-bearing URL is logged. */
  }
  const reason = event.reason
    .replace(/(?:https?|wss?):\/\/[^\s]+/gi, '[url]')
    .replace(/(?:Bearer\s+|(?:token|sessionId|authorization)=)[^\s&]+/gi, '[credential]')
    .replace(/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, '[credential]')
  return {
    code: event.code,
    reason: reason || null,
    wasClean: event.wasClean,
    phase: opened ? 'open' : 'handshake',
    attempt,
    host,
    diagnosis: event.code === 1006 ? 'abnormal_without_close_frame' : 'peer_close_frame',
  }
}
